import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CompanyJobApiResponse } from '../../models/company-insight.model';
import { DetailsUtilService } from '../../../details/services/details-utils.service';
import { FacebookShareUrl, LinkedinShareUrl, WhatsappShareUrl } from '../../../../shared/enums/app.enums';
import { environment } from '../../../../../environments/environment';
import { SharePlatform } from '../../utils/company-insight.utils';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-share-job-modal',
  imports: [TranslocoModule],
  templateUrl: './share-job-modal.html',
  styleUrl: './share-job-modal.scss',
})
export class ShareJobModal implements OnInit, OnDestroy {
  private readonly detailsUtilService = inject(DetailsUtilService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly renderer = inject(Renderer2);
  environment = environment;
  readonly job = input<CompanyJobApiResponse | null>(null);
  readonly url = input('');
  readonly title = input('');
  readonly showWhatsapp = input(false);
  readonly closed = output<void>();

  protected readonly copiedKey = 'Copied!' as const;
  protected readonly copyLabel = signal<'Copy' | 'Copied!'>('Copy');
  protected readonly selectedPlatform = signal<SharePlatform>('facebook');

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.addClass(document.body, 'overflow-hidden');
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  protected readonly ariaLabel = computed(
    () => `Share ${this.title().trim() || (this.job()?.jobTitle ?? 'job')}`
  );

  protected readonly jobUrl = computed(() =>
    this.url() || `${environment.jobDetailsBaseUrl}${this.job()?.Jobid ?? ''}`
  );

  protected readonly displayUrl = computed(() => {
    if (this.selectedPlatform() === 'linkedin') {
      return `${LinkedinShareUrl}${encodeURIComponent(this.jobUrl())}`;
    }
    return `${FacebookShareUrl}${encodeURIComponent(this.jobUrl())}`;
  });

  protected shareOnLinkedIn(): void {
    this.selectedPlatform.set('linkedin');
    this.detailsUtilService.socialMediaShare(LinkedinShareUrl, this.jobUrl());
  }

  protected shareOnFacebook(): void {
    this.selectedPlatform.set('facebook');
    this.detailsUtilService.socialMediaShare(FacebookShareUrl, this.jobUrl());
  }

  protected shareOnWhatsapp(): void {
    this.detailsUtilService.socialMediaShare(WhatsappShareUrl, this.jobUrl(), 'Check out this job!');
  }

  protected copyLink(): void {
    navigator.clipboard.writeText(this.jobUrl()).then(() => {
      this.copyLabel.set('Copied!');
      setTimeout(() => this.copyLabel.set('Copy'), 2000);
    });
  }

  protected close(): void {
    this.closed.emit();
  }
}